import { TaxonomyType, defaultLanguage } from '@stereoagency/constants';
import type { BadgeCardProps } from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { FiMapPin, FiClock } from 'react-icons/fi';
import flush from 'just-flush';
import compact from 'just-compact';
import {
  createDocumentURL,
  createIcsFile,
  dateFormatterFactory,
  findInKeyArray,
  formatProfileName
} from '@/utils';
import type { RouteResultType } from '@/app/(root)/api/data/event/route';

type Config = {
  t: ReturnType<typeof useTranslation>['t'];
};

export default function eventAsProps(
  event: RouteResultType[number],
  lang: string,
  config: Config
): BadgeCardProps | null {
  const title = event.title
    ? findInKeyArray(event.title, [lang, defaultLanguage.id])
    : null;

  if (!title?.value || !event.datetime || !event.slug?.current) {
    return null;
  }

  const url = createDocumentURL(lang, 'event', event.slug.current);

  const date = new Date(event.datetime);
  const duration = event.duration
    ? event.duration.split(':').map((v) => parseInt(v, 10))
    : null;
  const endDate = duration
    ? new Date(date.getTime() + (duration[0] * 60 + duration[1]) * 60000)
    : null;

  const timeFormatter = dateFormatterFactory(lang, {
    hour: '2-digit',
    minute: '2-digit'
  });
  const dayFormatter = dateFormatterFactory(lang, { day: '2-digit' });
  const monthYearFormatter = dateFormatterFactory(lang, {
    month: 'short',
    year: 'numeric'
  });

  const metadata = flush([
    event.location
      ? {
          icon: <FiMapPin />,
          title: event.location
        }
      : null,
    {
      icon: <FiClock />,
      title: endDate
        ? `${timeFormatter.format(date)} - ${timeFormatter.format(endDate)}`
        : timeFormatter.format(date)
    }
  ]);

  const eventType =
    event.eventType ??
    event.taxonomies?.find((tax) => tax.type === TaxonomyType.EVENT_TYPE);
  const label = eventType?.name
    ? findInKeyArray(eventType.name, [lang, defaultLanguage.id])
    : null;

  const contactMetaAction =
    event.contact?.email && event.contact.name
      ? {
          label: `${config.t('contact')}: `,
          action: {
            href: `mailto:${event.contact.email}`,
            label: formatProfileName(event.contact)
          }
        }
      : undefined;

  const lecturerMetaAction = event.lecturer
    ? {
        label: `${config.t('lecturer')}: `,
        action: {
          href: url,
          label: event.lecturer,
          className: 'pointer-events-none'
        }
      }
    : undefined;

  return {
    variant: 'row',
    title: title.value,
    label: label?.value ?? undefined,
    badge: {
      title: dayFormatter.format(date),
      label: monthYearFormatter.format(date)
    },
    metadata,
    action: {
      href: url
    },
    secondaryAction: {
      href: url,
      label: config.t('global:add-to-calendar'),
      onClick(ev) {
        ev.preventDefault();
        const ics = createIcsFile({
          url,
          id: event._id,
          title: title.value!,
          dateStart: date,
          dateEnd: endDate ?? undefined,
          contact:
            event.contact?.name && event.contact.email
              ? {
                  name: formatProfileName(event.contact),
                  email: event.contact.email
                }
              : undefined,
          location: event.location
        });
        const icsUrl = URL.createObjectURL(ics);
        window.open(icsUrl);
      }
    },
    metaActions: compact([contactMetaAction, lecturerMetaAction])
  };
}
